import { useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { api } from '../api/client'
import { KeyIcon } from '../components/Icons'

export function ResetPasswordPage() {
  const [params] = useSearchParams()
  const token = params.get('token') ?? ''
  const navigate = useNavigate()

  const [password, setPassword] = useState('')
  const [confirmation, setConfirmation] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  // A link that lost its query string cannot be completed, so say so rather than failing on submit.
  if (!token) {
    return (
      <div className="auth-wrap">
        <div className="auth-card">
          <h1>That link is incomplete</h1>
          <p className="muted" style={{ marginTop: 8 }}>
            It is missing its reset token. Mail clients sometimes cut long links in half — copying
            the whole thing from the email usually fixes it, or request a new one.
          </p>
          <Link className="link" to="/forgot-password" style={{ marginTop: 16, display: 'inline-block' }}>
            Request a new link
          </Link>
        </div>
      </div>
    )
  }

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    if (password !== confirmation) {
      setError('Those two passwords do not match.')
      return
    }
    setError(null)
    setBusy(true)
    try {
      await api.resetPassword(token, password)
      // The reset deliberately does not hand back a session, and the address the token belongs to
      // is never placed in the URL, so there is nothing to sign in with here. Send them to the
      // sign-in form with a flag that acknowledges the change. `replace` so the back button
      // cannot return to a form holding the new password, and so the spent token leaves history.
      navigate('/sign-in?reset=1', { replace: true })
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : 'Something went wrong')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="auth-wrap">
      <form className="auth-card" onSubmit={submit}>
        <h1>Choose a new password</h1>
        <p className="muted" style={{ marginTop: 8 }}>
          This also signs you out everywhere else, on the website and in the Android app.
        </p>

        <label htmlFor="password">New password</label>
        <input
          id="password"
          type="password"
          autoComplete="new-password"
          minLength={8}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        />
        <p className="muted" style={{ fontSize: 13, marginTop: 6 }}>
          At least 8 characters.
        </p>

        <label htmlFor="confirmation">Repeat it</label>
        <input
          id="confirmation"
          type="password"
          autoComplete="new-password"
          minLength={8}
          value={confirmation}
          onChange={(e) => setConfirmation(e.target.value)}
          required
        />

        {error && <div className="error">{error}</div>}

        <button className="primary" type="submit" disabled={busy}>
          <KeyIcon />
          {busy ? 'Saving…' : 'Set new password'}
        </button>

        <Link className="link" to="/sign-in" style={{ marginTop: 12, display: 'inline-block' }}>
          Back to sign in
        </Link>
      </form>
    </div>
  )
}
